// Statement parser for function calls like `@fun.make-chip(arg1, arg2);`.
// Accepts only full statement calls, not property-value inline usage.
import { findMatchingPair, splitTopLevelByComma } from '../../loops/for-loop/scanner-utils'
import { isValidFunctionIdentifier } from '../helpers'
import type { LuisFunctionCall } from '../types'

const STATEMENT_PREFIX = '@fun'

// Build function-call payloads from statement text when the syntax is valid.
export function parseFunctionCallStatement(statement: string): LuisFunctionCall | null {
  const trimmed = statement.trim()
  // Check whether the statement can be a standalone function call.
  if (!trimmed || hasNonPlaceholderBraces(trimmed)) {
    // Return null for empty text or block-like statements.
    return null
  } else if (!trimmed.startsWith(STATEMENT_PREFIX)) {
    // Reject statements without the required @fun statement prefix.
    // Return null for statements without the required `@fun` prefix.
    return null
  } else {
    // Continue statement call parsing for @fun-prefixed calls.
  }

  const cursor = consumeCallPrefix(trimmed)
  // Check whether the `@fun` call prefix is valid.
  if (cursor === -1) {
    // Return null when the `@fun` call prefix is invalid.
    return null
  } else {
    // Continue when @fun call prefix is valid.
  }

  // Skip spaces between function name and opening `(`.
  const nameStart = cursor
  const nameEnd = findNameEnd(trimmed, nameStart)
  const name = trimmed.slice(nameStart, nameEnd)
  // Check whether the parsed token is a valid function identifier.
  if (!isValidFunctionIdentifier(name)) {
    // Return null for invalid function identifiers.
    return null
  } else {
    // Continue argument parsing for valid function identifiers.
  }
  let argsStart = nameEnd
  // Skip spaces between function name and opening parenthesis.
  while (argsStart < trimmed.length && /\s/.test(trimmed[argsStart])) {
    argsStart += 1
  }

  // Check whether the parsed function name is followed by `(`.
  if (trimmed[argsStart] !== '(') {
    // Return null when the call has no opening parenthesis.
    return null
  } else {
    // Continue argument list parsing.
  }

  const argsEnd = findMatchingPair(trimmed, argsStart, '(', ')')
  // Check whether the call statement closes on the final character.
  if (argsEnd === -1 || argsEnd !== trimmed.length - 1) {
    // Return null for unbalanced or partial call statements.
    return null
  } else {
    // Continue argument extraction for balanced call statement.
  }

  const argsSource = trimmed.slice(argsStart + 1, argsEnd).trim()
  const args = argsSource ? splitTopLevelByComma(argsSource).map((entry) => entry.trim()) : []
  // Return parsed statement-level function call payload.
  return { name, args }
}

// Check whether statement text contains real block braces outside `${...}`.
function hasNonPlaceholderBraces(source: string): boolean {
  let quote: "'" | '"' | null = null
  let placeholderDepth = 0

  // Scan statement text while treating `${...}` braces as argument content.
  for (let index = 0; index < source.length; index += 1) {
    const char = source[index]
    const next = source[index + 1] ?? ''
    const previous = source[index - 1] ?? ''
    if (quote) {
      if (char === quote && previous !== '\\') {
        quote = null
      }
      continue
    }
    if (char === "'" || char === '"') {
      quote = char
      continue
    }
    if (char === '$' && next === '{') {
      placeholderDepth += 1
      index += 1
      continue
    }
    if (placeholderDepth > 0) {
      if (char === '{') {
        placeholderDepth += 1
      } else if (char === '}') {
        placeholderDepth -= 1
      }
      continue
    }
    if (char === '{' || char === '}') {
      // Return true when a real block brace appears outside placeholders.
      return true
    }
  }

  // Return false when braces only belong to placeholder arguments.
  return false
}

// Resolve the function-call identifier end index in statement text.
function findNameEnd(source: string, start: number): number {
  let cursor = start
  // Consume identifier and hyphen segments.
  while (cursor < source.length && /[a-zA-Z0-9_-]/.test(source[cursor])) {
    cursor += 1
  }
  // Return first index after parsed identifier token.
  return cursor
}

// Resolve the `@fun` call prefix and return the function-name start index.
function consumeCallPrefix(statement: string): number {
  const cursor = STATEMENT_PREFIX.length
  // Check whether the statement ends right after the prefix.
  if (!statement[cursor]) {
    // Return `-1` when the statement ends right after `@fun`.
    return -1
  } else if (statement[cursor] === '.') {
    // Return index right after dot-prefixed call marker.
    return cursor + 1
  } else if (!/\s/.test(statement[cursor])) {
    // Return `-1` for invalid prefix separators.
    return -1
  } else {
    // Reject whitespace-prefixed call marker; dot prefix is required.
    // Return `-1` for legacy whitespace-prefixed markers.
    return -1
  }
}
